import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Login, Logout } from '@carbon/icons-react';
import {
  Content,
  Header,
  HeaderContainer,
  HeaderGlobalAction,
  HeaderGlobalBar,
  HeaderMenuButton,
  HeaderMenuItem,
  HeaderName,
  HeaderNavigation,
  SideNav,
  SideNavItems,
  SideNavLink,
  SkipToContent,
  Theme,
} from '@carbon/react';
import { get, type Me } from '@/lib/api';
import { signOut, useSession } from '@/lib/auth-client';

const NAV = [
  { to: '/', label: 'This week' },
  { to: '/competitors', label: 'Competitors' },
  { to: '/directory', label: 'Directory' },
  { to: '/battlecards', label: 'Battlecards' },
  { to: '/you', label: 'You' },
] as const;

export function useMe() {
  const { data: session } = useSession();
  return useQuery({
    queryKey: ['me'],
    queryFn: () => get<Me>('/v1/me'),
    enabled: Boolean(session?.user),
  });
}

export function Shell() {
  const { data: session, isPending } = useSession();
  const { data: me } = useMe();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const account = me?.account.email ?? session?.user?.email ?? 'Account';
  const accountDescription = me
    ? `${account}, ${planName(me.account.plan)}, watching ${me.watching}${me.watchlist_limit ? ` of ${me.watchlist_limit}` : ''}. Sign out`
    : `${account}. Sign out`;

  const isActive = (to: string) => to === '/' ? path === '/' : path.startsWith(to);

  return (
    <div className="min-h-dvh">
      <HeaderContainer
        render={({ isSideNavExpanded, onClickSideNavExpand }) => (
          <Theme theme="g100">
            <Header aria-label="moves.watch" className="print:hidden">
              <SkipToContent />
              {session?.user ? (
                <HeaderMenuButton
                  aria-label={isSideNavExpanded ? 'Close navigation' : 'Open navigation'}
                  isActive={isSideNavExpanded}
                  onClick={onClickSideNavExpand}
                />
              ) : null}
              <HeaderName as={Link} to="/" prefix="">moves.watch</HeaderName>
              {session?.user ? (
                <HeaderNavigation aria-label="Primary navigation">
                  {NAV.map((item) => (
                    <HeaderMenuItem
                      key={item.to}
                      as={Link}
                      to={item.to}
                      isActive={isActive(item.to)}
                      aria-current={isActive(item.to) ? 'page' : undefined}
                    >
                      {item.label}
                    </HeaderMenuItem>
                  ))}
                </HeaderNavigation>
              ) : null}
              <HeaderGlobalBar>
                {session?.user ? (
                  <HeaderGlobalAction
                    aria-label={accountDescription}
                    tooltipAlignment="end"
                    onClick={() => void signOut().then(() => window.location.assign('/sign-in'))}
                  >
                    <Logout size={20} />
                  </HeaderGlobalAction>
                ) : isPending ? null : (
                  <HeaderGlobalAction
                    aria-label="Sign in"
                    tooltipAlignment="end"
                    onClick={() => window.location.assign('/sign-in')}
                  >
                    <Login size={20} />
                  </HeaderGlobalAction>
                )}
              </HeaderGlobalBar>
            </Header>
            {session?.user ? (
              <SideNav
                aria-label="Primary navigation"
                expanded={isSideNavExpanded}
                isPersistent={false}
                onSideNavBlur={onClickSideNavExpand}
                className="print:hidden"
              >
                <SideNavItems>
                  {NAV.map((item) => (
                    <SideNavLink
                      key={item.to}
                      as={Link}
                      to={item.to}
                      isActive={isActive(item.to)}
                      onClick={isSideNavExpanded ? onClickSideNavExpand : undefined}
                    >
                      {item.label}
                    </SideNavLink>
                  ))}
                </SideNavItems>
              </SideNav>
            ) : null}
          </Theme>
        )}
      />
      <Theme theme="white">
        <Content id="main-content" className="app-main p-0">
          <div className="app-briefing-bar print:hidden">
            <p>Competitive intelligence / weekly evidence desk</p>
            <p className="app-briefing-status">
              <span aria-hidden />
              {me ? `${me.watching} companies under watch` : 'Evidence, not alerts'}
            </p>
          </div>
          <div className="app-workspace">
            <Outlet />
          </div>
        </Content>
      </Theme>
    </div>
  );
}

function planName(plan: string): string {
  return plan === 'team' ? 'Scale' : plan === 'pro' ? 'Pro' : 'Free';
}
